import { useState } from "react";
import { useConnection } from "wagmi";
import { Link } from "../components/Link";
import { Dot } from "../components/ui";
import { ConnectButton } from "../wallet/ConnectButton";
import { shortAddress } from "../wallet/address";
import { useStreamStatus } from "../state/account";
import { useSummary, useQueue } from "../state/market";
import { useValuations } from "../state/valuation";
import { big, usd } from "../lib/num";
import type { Route } from "../router";
import { AccountSheet } from "./AccountSheet";

export const NAV: { view: Route["view"]; label: string; href: string }[] = [
  { view: "trade", label: "Trade", href: "/trade/BTC" },
  { view: "portfolio", label: "Portfolio", href: "/portfolio" },
  { view: "staking", label: "Staking", href: "/staking" },
  { view: "stats", label: "Stats", href: "/stats" },
];

/** The LP's cash and whether winners are waiting: the venue's health at a glance. */
function VenueChip() {
  const summary = useSummary().data;
  const queue = useQueue().data;
  if (!summary) return null;
  const queued = big(queue?.total ?? summary.queue) > 0n;
  return (
    <Link href="/stats" className="venue-chip" title={queued ? "Winners are waiting in the payout queue" : "The LP pays winners now"}>
      <Dot tone={queued ? "warn" : "up"} />
      <span className="label">LP</span>
      <span className="num">{usd(summary.lp, { compact: 4, places: 2 })}</span>
      {queued && <span className="num warn">· queue {usd(queue?.total ?? summary.queue, { compact: 4 })}</span>}
    </Link>
  );
}

function AccountButton() {
  const { address, status } = useConnection();
  const stream = useStreamStatus();
  const valued = useValuations();
  const [open, setOpen] = useState(false);
  if (status !== "connected" || !address) return <ConnectButton size="sm" />;
  const tone = stream === "live" ? "up" : stream === "off" ? "off" : "warn";
  return (
    <>
      <button type="button" className="account-btn" onClick={() => setOpen(true)} aria-label="Account">
        <Dot tone={tone} />
        {valued && <span className="num account-equity">{usd(valued.equity)}</span>}
        <span className="mono account-addr">{shortAddress(address.toLowerCase())}</span>
      </button>
      <AccountSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function TopBar({ route }: { route: Route }) {
  return (
    <header className="topbar">
      <div className="topbar-inner">
        <Link className="wordmark" href="/trade/BTC" aria-label="papertrade home">
          papertrade<span>_</span>
        </Link>
        <nav className="nav" aria-label="Primary">
          {NAV.map((item) => (
            <Link key={item.view} href={item.href} aria-current={route.view === item.view ? "page" : undefined}>
              {item.label}
            </Link>
          ))}
        </nav>
        <span className="topbar-spacer" />
        <VenueChip />
        <AccountButton />
      </div>
    </header>
  );
}

export function TabBar({ route }: { route: Route }) {
  return (
    <nav className="tabbar" aria-label="Primary">
      {NAV.map((item) => (
        <Link key={item.view} href={item.href} className="tabbar-item" aria-current={route.view === item.view ? "page" : undefined}>
          <TabIcon view={item.view} />
          <span>{item.label}</span>
        </Link>
      ))}
    </nav>
  );
}

function TabIcon({ view }: { view: Route["view"] }) {
  const common = { width: 20, height: 20, viewBox: "0 0 20 20", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (view) {
    case "trade":
      return (
        <svg {...common} aria-hidden>
          <path d="M3 14l4-5 3 3 6-7" />
          <path d="M12 5h4v4" />
        </svg>
      );
    case "portfolio":
      return (
        <svg {...common} aria-hidden>
          <rect x="3" y="5" width="14" height="11" rx="2" />
          <path d="M3 9h14M13 12.5h1.5" />
        </svg>
      );
    case "staking":
      return (
        <svg {...common} aria-hidden>
          <path d="M5 4h7l3 3v9H5z" />
          <path d="M8 10h4M8 13h4" />
        </svg>
      );
    case "stats":
      return (
        <svg {...common} aria-hidden>
          <path d="M4 16V9M10 16V4M16 16v-5" />
        </svg>
      );
  }
}
